// QuestionRenderer - Dispatcher que renderiza el componente correcto según tipo de pregunta
// Sprint 3 - Actualizado para arquitectura Sections + Categories

import React from 'react';
import { 
  Box,
  Typography
} from '@mui/material';
import { PreguntaCompleta } from '../../../types/formulario.types';
import { QuestionResponse } from '../../../types/audit.types';
import CameraCapture from '../../../components/CameraCapture';

// Componentes de preguntas (se crearán a continuación)
import QuestionSiNo from './questions/QuestionSiNo';
import QuestionEscala from './questions/QuestionEscala';
import QuestionMultiple from './questions/QuestionMultiple';
import QuestionSeleccionMultiple from './questions/QuestionSeleccionMultiple';
import QuestionTexto from './questions/QuestionTexto';
import QuestionNumero from './questions/QuestionNumero';
import QuestionFecha from './questions/QuestionFecha';
import QuestionHora from './questions/QuestionHora';

interface QuestionRendererProps {
  pregunta: PreguntaCompleta;
  index: number;
  categoriaId: number;
  categoryName?: string;
  categoryColor?: string;
  respuesta?: QuestionResponse;
  onChange: (preguntaId: number, categoriaId: number, valor: any, comentario?: string) => void;
  auditoriaId: number;
  evidenciasCount?: number;
  onEvidenciasUpdated?: () => void;
}

const QuestionRenderer: React.FC<QuestionRendererProps> = ({
  pregunta,
  categoriaId,
  categoryName,
  categoryColor,
  respuesta,
  onChange,
  auditoriaId,
  evidenciasCount = 0,
  onEvidenciasUpdated,
}) => {
  const handleChange = (valor: any, comentario?: string) => {
    onChange(pregunta.id, categoriaId, valor, comentario);
  };

  const handleUploadSuccess = () => {
    if (onEvidenciasUpdated) {
      onEvidenciasUpdated();
    }
  };

  // Renderizar el componente correcto según el tipo
  const renderQuestion = () => {
    const commonProps = {
      pregunta,
      respuesta,
      onChange: handleChange,
    };

    switch ((pregunta as any).type) {
      case 'yes_no':
        return <QuestionSiNo {...commonProps} />;
      
      case 'scale':
        return <QuestionEscala {...commonProps} />;
      
      case 'select':
        return <QuestionMultiple {...commonProps} />;
      
      case 'multiple':
        return <QuestionSeleccionMultiple {...commonProps} />;
      
      case 'text':
        return <QuestionTexto {...commonProps} />;
      
      case 'number':
        return <QuestionNumero {...commonProps} />;
      
      case 'date':
        return <QuestionFecha {...commonProps} />;
      
      case 'time':
        return <QuestionHora {...commonProps} />;
      
      default:
        return (
          <Box sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
            Tipo de pregunta no soportado: {(pregunta as any).type}
          </Box>
        );
    }
  };

  return (
    <Box
      sx={{
        py: { xs: 2, sm: 2.5 },
        px: { xs: 2, sm: 2.5, md: 3 },
        mb: { xs: 2, sm: 2.5 },
        bgcolor: '#f5f5f5',
        border: '1px solid #000000',
        borderRadius: 1,
      }}
    >
      {/* Badge de categoría - Solo si se proporciona */}
      {categoryName && (
        <Box sx={{ mb: 1 }}>
          <Typography
            component="span"
            sx={{
              fontSize: { xs: '0.65rem', sm: '0.7rem' },
              fontWeight: 700,
              color: categoryColor || 'primary.main',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            {categoryName}
          </Typography>
        </Box>
      )}
      
      {renderQuestion()}
      
      {/* Botones de evidencias - Directamente visibles sin desplegable */}
      {(pregunta as any).allows_evidence && (
        <Box sx={{ 
          mt: { xs: 1.5, sm: 2 }, 
          pt: { xs: 1.5, sm: 2 }, 
          borderTop: '1px dashed',
          borderColor: 'divider' 
        }}>
          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ 
              display: 'block', 
              mb: 0.75,
              fontWeight: 600,
              fontSize: { xs: '0.625rem', sm: '0.7rem' },
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            📸 Evidencias {evidenciasCount > 0 && `(${evidenciasCount})`}
          </Typography>
          
          {/* CameraCapture siempre visible - sin botón de toggle */}
          <Box>
            <CameraCapture
              auditoriaId={auditoriaId}
              preguntaId={pregunta.id}
              onUploadSuccess={handleUploadSuccess}
              maxImages={5}
              autoCompress={true}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default QuestionRenderer;
